import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FaTimes, FaFilter, FaVenus, FaMars, FaVenusMars, FaHeart, FaMapMarkerAlt, FaSort } from 'react-icons/fa';

Modal.setAppElement('#root');

const FilterModal = ({ isOpen, onClose, onApply, currentFilters = {} }) => {
  const [filters, setFilters] = useState({
    sexual_preference: currentFilters.sexual_preference || '3',
    sexual_orientation: currentFilters.sexual_orientation || '1',
    country: currentFilters.country || '',
    order_by: currentFilters.order_by || '1',
    age_range: currentFilters.age_range || [18, 100],
    distance_range: currentFilters.distance_range || [0, 100],
    location_dating: currentFilters.location_dating || false,
  });

  useEffect(() => {
    if (isOpen) {
      setFilters({
        sexual_preference: currentFilters.sexual_preference || '3',
        sexual_orientation: currentFilters.sexual_orientation || '1',
        country: currentFilters.country || '',
        order_by: currentFilters.order_by || '1',
        age_range: currentFilters.age_range || [18, 100],
        distance_range: currentFilters.distance_range || [0, 100],
        location_dating: currentFilters.location_dating || false,
      });
    }
  }, [isOpen, currentFilters]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onApply(filters);
  };

  const handleReset = () => {
    setFilters({
      sexual_preference: '3',
      sexual_orientation: '1',
      country: '',
      order_by: '1',
      age_range: [18, 100],
      distance_range: [0, 100],
      location_dating: false,
    });
  };

  const getGenderIcon = (value) => {
    switch (value) {
      case '1': return <FaMars className="text-blue-500" />;
      case '2': return <FaVenus className="text-pink-500" />;
      case '3': return <FaVenusMars className="text-purple-500" />;
      default: return <FaVenusMars className="text-gray-500" />;
    }
  };

  const getOrientationIcon = (value) => {
    return <FaHeart className="text-red-500" />;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="filter-modal-content"
      overlayClassName="modal-overlay-backdrop"
      closeTimeoutMS={200}
      style={{
        content: {
          position: 'relative',
          background: 'transparent',
          border: 'none',
          borderRadius: '20px',
          padding: 0,
          maxWidth: '600px',
          width: '95vw',
          maxHeight: '90vh',
          overflow: 'hidden'
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 1000
        }
      }}
    >
      <div className="filter-modal-container">
        {/* Fixed Header */}
        <div className="filter-modal-header">
          <div className="flex items-center space-x-3">
            <div className="bg-pink-100 p-2 rounded-full">
              <FaFilter className="text-pink-500 text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Filter Results</h2>
              <p className="text-sm text-gray-600">Find your perfect match</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FaTimes className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="filter-modal-body">
          <div className="space-y-4 sm:space-y-5 md:space-y-6">
            {/* Gender Preferences */}
            <div className="filter-section">
              <label className="filter-section-title">
                <FaVenusMars className="text-purple-500 mr-2" />
                I'm interested in
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                {[
                  { value: '3', label: 'Everyone', icon: <FaVenusMars className="text-purple-500" /> },
                  { value: '1', label: 'Men', icon: <FaMars className="text-blue-500" /> },
                  { value: '2', label: 'Women', icon: <FaVenus className="text-pink-500" /> },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFilters({ ...filters, sexual_preference: option.value })}
                    className={`filter-option-button ${
                      filters.sexual_preference === option.value
                        ? 'filter-option-active'
                        : 'filter-option-inactive'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      {option.icon}
                      <span className="text-xs font-medium">{option.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sexual Orientation */}
            <div className="filter-section">
              <label className="filter-section-title">
                <FaHeart className="text-red-500 mr-2" />
                My sexual orientation
              </label>
              <select
                value={filters.sexual_orientation}
                onChange={(e) => setFilters({ ...filters, sexual_orientation: e.target.value })}
                className="filter-select"
              >
                <option value="1">Straight</option>
                <option value="2">Gay</option>
                <option value="3">Lesbian</option>
                <option value="4">Bisexual</option>
                <option value="5">Asexual</option>
                <option value="6">Other</option>
              </select>
            </div>

            {/* Age Range */}
            <div className="filter-section">
              <label className="filter-section-title">
                🎂 Age Range
              </label>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Minimum: {filters.age_range[0]}</span>
                  <span className="text-sm text-gray-600">Maximum: {filters.age_range[1]}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min Age</label>
                    <input
                      type="range"
                      min="18"
                      max="80"
                      value={filters.age_range[0]}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          age_range: [parseInt(e.target.value), filters.age_range[1]]
                        })
                      }
                      className="filter-range"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max Age</label>
              <input
                type="range"
                min="18"
                max="100"
                value={filters.age_range[1]}
                onChange={(e) =>
                        setFilters({
                          ...filters,
                          age_range: [filters.age_range[0], parseInt(e.target.value)]
                        })
                }
                      className="filter-range"
                    />
                  </div>
                </div>
                <div className="text-center text-sm text-gray-700 font-medium">
                  {filters.age_range[0]} - {filters.age_range[1]} years old
                </div>
              </div>
            </div>

            {/* Location-based Dating */}
            <div className="filter-section">
              <label className="filter-toggle">
                <input
                  type="checkbox"
                  checked={filters.location_dating}
                  onChange={(e) => setFilters({ ...filters, location_dating: e.target.checked })}
                  className="filter-checkbox"
                />
                <span className="filter-toggle-slider"></span>
                <span className="filter-toggle-text">
                  <FaMapMarkerAlt className="mr-2" />
                  Location-based matching
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Show people near your current location
              </p>
            </div>

            {/* Distance Range */}
            {filters.location_dating && (
              <div className="filter-section">
                <label className="filter-section-title">
                  📍 Distance Range
                </label>
                <div className="space-y-3">
                <input
                  type="range"
                  min="0"
                  max="500"
                  value={filters.distance_range[1]}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      distance_range: [filters.distance_range[0], parseInt(e.target.value)],
                    })
                  }
                    className="filter-range"
                />
                  <div className="text-center text-sm text-gray-700 font-medium">
                    Within {filters.distance_range[1]} km
                  </div>
                </div>
              </div>
            )}

            {/* Order By */}
            <div className="filter-section">
              <label className="filter-section-title">
                <FaSort className="text-gray-500 mr-2" />
                Sort by
              </label>
              <select
                value={filters.order_by}
                onChange={(e) => setFilters({ ...filters, order_by: e.target.value })}
                className="filter-select"
              >
                <option value="1">Newest members</option>
                <option value="2">Oldest members</option>
                <option value="3">Recently online</option>
                <option value="4">Random</option>
                <option value="5">Best match</option>
              </select>
            </div>
          </div>

        </form>

        {/* Fixed Footer */}
        <div className="filter-modal-footer">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="filter-modal-btn filter-modal-reset"
            >
              Reset All
            </button>
            <button
              type="button"
              onClick={onClose}
              className="filter-modal-btn filter-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onApply(filters)}
              className="filter-modal-btn filter-modal-apply"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FilterModal;

